// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Why is there no single price?',
      answer: 'Cost tracks the destination, how many applications are involved, and how much support you want from us. We look at each case on its own terms rather than applying one flat rate to everybody.'
    },
    {
      question: 'Is that first consultation genuinely free of charge?',
      answer: 'Yes. It costs nothing and exists so you can decide on good information. Nobody has to continue afterwards, and if we think our services are not right for your situation we will tell you so.'
    },
    {
      question: 'What about refunds?',
      answer: 'That depends on how far the work has already progressed, and the terms are spelled out to you before anything is paid. Our position on refunds is never something you find out about afterwards.'
    },
    {
      question: 'Can payment be split up?',
      answer: 'Sometimes, depending on the service and your circumstances. Raise it during the consultation and we will tell you honestly what can be arranged.'
    },
    {
      question: 'Do you take a cut of my tuition or my wages?',
      answer: 'No. Ivipass earns nothing from your tuition, your salary or any scholarship you are awarded. We are paid for advisory and support work, at a rate quoted to you in advance.'
    },
    {
      question: 'At what point does payment fall due?',
      answer: 'After the consultation, once the scope is agreed and the services are confirmed. You will never be asked to pay for something that has not already been explained to you.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Questions About Fees
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-surface rounded-lg shadow-sm overflow-hidden"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
              >
                <span className="text-lg font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-secondary" />
                  ) : (
                    <Plus size={24} className="text-secondary" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-base font-light leading-relaxed">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
